import { useEffect, useState } from "react";
import { CircularProgress, Box, Typography, Container, Alert, Button, Paper } from "@mui/material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useGlobalState } from "./context/global-context";
import { useHGlobalController } from "./context/global-controller";
import { formatEntityName, getEntityCityName } from "../../utils/entity-formatter";

interface AppInitializerProps {
    children: React.ReactNode;
}

export function AppInitializer({ children }: AppInitializerProps) {
    const { applicationData, parametersPortal, error } = useGlobalState();
    const [initialLoading, setInitialLoading] = useState(true);
    const { getData } = useHGlobalController();

    useEffect(() => {
        const onInit = async () => {
            await getData();
            setInitialLoading(false);
        };

        if (applicationData === undefined && !error) {
            onInit();
        } else {
            setInitialLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Atualiza título e favicon quando os dados da entidade são carregados
    useEffect(() => {
        if (applicationData?.entity) {
            // Atualiza o título da página
            const entityDisplay = formatEntityName(applicationData.entity.name);
            const entityTitle = entityDisplay.type && entityDisplay.name
                ? `PORTAL DA ${entityDisplay.type} ${entityDisplay.name}`
                : entityDisplay.fullName
                    ? `PORTAL DA ${entityDisplay.fullName}`
                    : 'PORTAL DA ENTIDADE';
            document.title = entityTitle;

            // Atualiza o favicon
            const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
            if (favicon && applicationData.entity.brandLogo) {
                favicon.href = `data:image/png;base64,${applicationData.entity.brandLogo}`;
            } else if (favicon && applicationData.entity.stateBrandLogo) {
                favicon.href = applicationData.entity.stateBrandLogo;
            }
        }
    }, [applicationData]);

    // Dispara evento de atualização de tema quando parametersPortal são carregados
    useEffect(() => {
        if (parametersPortal?.theme !== undefined) {
            const event = new CustomEvent('portalThemeUpdate', {
                detail: { themeNumber: parametersPortal.theme }
            });
            window.dispatchEvent(event);
        }
    }, [parametersPortal]);

    const handleRetry = () => {
        setInitialLoading(true);
        getData().finally(() => setInitialLoading(false));
    };

    const loadingComponent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Animated background circles */}
            <Box
                sx={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'rgba(33, 150, 243, 0.1)',
                    top: '-150px',
                    left: '-150px',
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                        '0%, 100%': { transform: 'translate(0, 0)' },
                        '50%': { transform: 'translate(50px, 50px)' },
                    },
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(33, 150, 243, 0.1)',
                    bottom: '-100px',
                    right: '-100px',
                    animation: 'float 8s ease-in-out infinite',
                }}
            />

            {/* Main loading card */}
            <Paper
                elevation={10}
                sx={{
                    p: 5,
                    borderRadius: 4,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    minWidth: '300px',
                    zIndex: 1,
                }}
            >
                {/* Animated logo/icon */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        mb: 3,
                    }}
                >
                    <CircularProgress
                        size={80}
                        thickness={4}
                        sx={{
                            color: '#1976d2',
                            animationDuration: '1.5s',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 'bold',
                                color: '#1976d2',
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.5 },
                                },
                            }}
                        >
                            {getEntityCityName(applicationData?.entity?.name)?.charAt(0) || 'P'}
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 1,
                    }}
                >
                    {(() => {
                        if (!applicationData?.entity?.name) return 'PORTAL DA ENTIDADE';
                        const entityDisplay = formatEntityName(applicationData.entity.name);
                        return entityDisplay.type && entityDisplay.name
                            ? `PORTAL DA ${entityDisplay.type} ${entityDisplay.name}`
                            : `PORTAL DA ${entityDisplay.fullName}`;
                    })()}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        animation: 'fadeIn 1.5s ease-in-out infinite',
                        '@keyframes fadeIn': {
                            '0%, 100%': { opacity: 0.5 },
                            '50%': { opacity: 1 },
                        },
                    }}
                >
                    Carregando informações...
                </Typography>

                {/* Progress dots */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#1976d2',
                                animation: `bounce 1.4s ease-in-out ${index * 0.2}s infinite`,
                                '@keyframes bounce': {
                                    '0%, 80%, 100%': {
                                        transform: 'scale(0)',
                                    },
                                    '40%': {
                                        transform: 'scale(1)',
                                    },
                                },
                            }}
                        />
                    ))}
                </Box>
            </Paper>
        </Box>
    );

    const errorComponent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                px: 2,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Animated background circles */}
            <Box
                sx={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'rgba(33, 150, 243, 0.1)',
                    top: '-150px',
                    left: '-150px',
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                        '0%, 100%': { transform: 'translate(0, 0)' },
                        '50%': { transform: 'translate(50px, 50px)' },
                    },
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(33, 150, 243, 0.1)',
                    bottom: '-100px',
                    right: '-100px',
                    animation: 'float 8s ease-in-out infinite',
                }}
            />

            <Container maxWidth="sm">
                <Paper
                    elevation={12}
                    sx={{
                        p: 5,
                        textAlign: 'center',
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {/* Animated error icon */}
                    <Box
                        sx={{
                            display: 'inline-flex',
                            position: 'relative',
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'shake 0.5s ease-in-out',
                                '@keyframes shake': {
                                    '0%, 100%': { transform: 'translateX(0)' },
                                    '25%': { transform: 'translateX(-10px)' },
                                    '75%': { transform: 'translateX(10px)' },
                                },
                            }}
                        >
                            <ErrorOutlineIcon
                                sx={{
                                    fontSize: 60,
                                    color: 'white',
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        fontWeight={700}
                        sx={{
                            color: '#1976d2',
                        }}
                    >
                        {error?.statusCode === 500 ? 'Ops! Algo deu errado' : 'Falha na Conexão'}
                    </Typography>

                    {error?.statusCode !== 500 && error?.message && (
                        <Alert
                            severity="info"
                            sx={{
                                mt: 3,
                                mb: 3,
                                textAlign: 'left',
                                borderRadius: 2,
                                '& .MuiAlert-icon': {
                                    fontSize: 28,
                                },
                            }}
                        >
                            <Typography variant="body2" component="div" fontWeight={600}>
                                Detalhes do erro:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {error.message}
                            </Typography>
                            {error.statusCode && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 1,
                                        fontFamily: 'monospace',
                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                        padding: '4px 8px',
                                        borderRadius: 1,
                                        display: 'inline-block',
                                    }}
                                >
                                    HTTP {error.statusCode}
                                </Typography>
                            )}
                        </Alert>
                    )}

                    {error?.statusCode === 500 && (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                            Não foi possível carregar a aplicação. Nossa equipe já foi notificada.
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<RefreshIcon />}
                            onClick={handleRetry}
                            sx={{
                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                boxShadow: '0 4px 15px 0 rgba(25, 118, 210, 0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                                    boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.6)',
                                },
                            }}
                        >
                            Tentar Novamente
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            endIcon={<OpenInNewIcon />}
                            href="https://www.airdoc.com.br/"
                            target="_blank"
                            sx={{
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                    borderColor: '#1565c0',
                                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                },
                            }}
                        >
                            Site da AirDoc
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );

    if (initialLoading) {
        return loadingComponent;
    }

    if (error) {
        return errorComponent;
    }

    return <>{children}</>;
}
